import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { NoAccessDialogComponent } from '../../components/no-access-dialog/no-access-dialog.component';
import { LoginService } from '../login/login.service';
import { TokenExpirationService } from '../token-expiration/token-expiration.service';

@Injectable({
  providedIn: 'root',
})
export class SessionTimeoutService {
  private timeoutId: any;
  private monitoring = false;
  private hasHandledTokenExpiration = false;
  private lastResetTime = 0;

  // Events we listen to for user activity
  private readonly activityEvents = [
    'mousedown',
    'mousemove',
    'pointerdown',
    'keydown',
    'wheel',
    'scroll',
    'contextmenu',
  ];

  private readonly TIMEOUT_DURATION = 180 * 60 * 1000; // 180 minutes in milliseconds
  private readonly THROTTLE_DELAY = 1000; // 1 second - limits how often we process activity events

  // Bound version of resetTimer for event listeners
  private resetTimerBound = this.resetTimer.bind(this);

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private loginService: LoginService,
    private tokenExpirationService: TokenExpirationService
  ) {}

  startMonitoring(): void {
    // Only start if user is logged in
    if (!this.loginService.isLoggedIn()) {
      return; // Skip silently when anonymous/on login page
    }

    // Token expiration check disabled - only using inactivity timeout
    // if (this.tokenExpirationService.isTokenExpired()) {
    //   if (!this.hasHandledTokenExpiration) {
    //     this.hasHandledTokenExpiration = true;
    //     this.tokenExpirationService.handleTokenExpiration();
    //   }
    //   return;
    // }

    // Reset the flag since token is valid
    this.hasHandledTokenExpiration = false;

    if (this.monitoring) {
      // Already monitoring, just reset timer
      this.resetTimer();
      return;
    }
    this.activityEvents.forEach((event) => {
      document.addEventListener(event, this.resetTimerBound, { passive: true });
    });
    this.monitoring = true;
    this.resetTimer();

    // Token expiration monitoring disabled - only using inactivity timeout
    // this.tokenExpirationService.startTokenExpirationMonitoring();

    // eslint-disable-next-line no-console
    // console.log('[SessionTimeout] Monitoring started');
  }

  stopMonitoring(): void {
    if (!this.monitoring) return;
    this.activityEvents.forEach((event) => {
      document.removeEventListener(event, this.resetTimerBound);
    });
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.monitoring = false;
    this.hasHandledTokenExpiration = false; // Reset the flag when stopping
    this.lastResetTime = 0; // Reset throttle timer

    // Token expiration monitoring disabled - only using inactivity timeout
    // this.tokenExpirationService.stopTokenExpirationMonitoring();

    // eslint-disable-next-line no-console
    // console.log('[SessionTimeout] Monitoring stopped');
  }

  private resetTimer(): void {
    // Throttle: only process if enough time has passed since last reset
    const now = Date.now();
    if (now - this.lastResetTime < this.THROTTLE_DELAY) {
      return; // Skip this event, too soon since last reset
    }
    this.lastResetTime = now;

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    // Token expiration check disabled - only using inactivity timeout
    // if (this.tokenExpirationService.isTokenExpired()) {
    //   if (!this.hasHandledTokenExpiration) {
    //     this.hasHandledTokenExpiration = true;
    //     this.tokenExpirationService.handleTokenExpiration();
    //   }
    //   return;
    // }

    this.timeoutId = setTimeout(() => {
      this.handleTimeout();
    }, this.TIMEOUT_DURATION);
  }

  private handleTimeout(): void {
    // If user is no longer logged in (e.g., already at login), don't show timeout dialog
    if (!this.loginService.isLoggedIn()) {
      this.stopMonitoring();
      return;
    }
    // Clear session storage and redirect to login
    localStorage.clear();
    this.stopMonitoring();
    this.router.navigate(['/login']).then(() => {
      this.dialog.open(NoAccessDialogComponent, {
        width: '550px',
        data: {
          title: 'Sesión cerrada',
          message: 'Sesión cerrada debido a la inactividad',
          confirmText: 'Cerrar',
          cancelText: 'Cancelar',
        },
      });
    });
  }
}
